import { useState, useMemo, useCallback } from 'react';
import {
  Stack,
  Group,
  Title,
  Text,
  Button,
  Tabs,
  Loader,
  Alert,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  IconPlus,
  IconAlertCircle,
  IconFolderOpen,
  IconCategory,
  IconListTree,
} from '@tabler/icons-react';
import { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import { LISTLIST_TYPE_LABELS } from '@/lib/constants/appContent';
import { useAppContentListlists } from '../useAppContentListlists';
import { useAppContentPositions } from '../useAppContentPositions';
import { useCreateListlist, useDeleteListlist } from '../useAppContentMutations';
import { AppContentUtils } from '../AppContentUtils';
import { AppContentTable } from './AppContentTable';
import { CreateListlistDialog } from './CreateListlistDialog';
import type { ContentTableRow } from '../AppContentUtils';
import type { ListlistFormSchemaData } from '../schemas/ListlistForm.schema';

const VALID_TABS = ['groups', 'categories', 'series'] as const;
type ContentTab = (typeof VALID_TABS)[number];

function parseTab(raw: unknown): ContentTab {
  if (typeof raw === 'string' && VALID_TABS.includes(raw as ContentTab)) {
    return raw as ContentTab;
  }
  return 'groups';
}

const TAB_TO_TYPE: Record<ContentTab, ListlistTypeId> = {
  groups: ListlistTypeId.Root,
  categories: ListlistTypeId.Category,
  series: ListlistTypeId.Series,
};

const TAB_CHILD_LABELS: Record<ContentTab, string> = {
  groups: 'Categories',
  categories: 'Series',
  series: 'Seasons',
};

const TAB_DETAIL_ROUTES: Record<ContentTab, string> = {
  groups: '/apps/content/groups',
  categories: '/apps/content/categories',
  series: '/apps/content/series',
};

export function AppContentPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false }) as Record<string, unknown>;
  const activeTab = parseTab(searchParams.tab);

  const {
    contentGroups,
    categories,
    series,
    isLoading,
    isError,
    error,
  } = useAppContentListlists();

  const { data: positions } = useAppContentPositions();
  const createMutation = useCreateListlist();
  const deleteMutation = useDeleteListlist();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Build child count maps from all positions data
  const childCountMap = useMemo(() => {
    if (!positions) return new Map<number, number>();
    const allNodes = [
      ...(positions.rootNodes ?? []),
      ...(positions.categoryNodes ?? []),
      ...(positions.seriesNodes ?? []),
    ];
    return AppContentUtils.buildChildCountMap(allNodes);
  }, [positions]);

  // Build table rows for each tab
  const groupRows: ContentTableRow[] = useMemo(
    () =>
      contentGroups.map((dto) =>
        AppContentUtils.buildTableRow(dto, childCountMap.get(dto.llr.llId) ?? 0),
      ),
    [contentGroups, childCountMap],
  );

  const categoryRows: ContentTableRow[] = useMemo(
    () =>
      categories.map((dto) =>
        AppContentUtils.buildTableRow(dto, childCountMap.get(dto.llr.llId) ?? 0),
      ),
    [categories, childCountMap],
  );

  const seriesRows: ContentTableRow[] = useMemo(
    () =>
      series.map((dto) =>
        AppContentUtils.buildTableRow(dto, childCountMap.get(dto.llr.llId) ?? 0),
      ),
    [series, childCountMap],
  );

  const currentRows: Record<ContentTab, ContentTableRow[]> = {
    groups: groupRows,
    categories: categoryRows,
    series: seriesRows,
  };

  const handleTabChange = useCallback(
    (tab: string | null) => {
      if (tab && VALID_TABS.includes(tab as ContentTab)) {
        navigate({
          to: '/apps/content',
          search: { tab } as Record<string, string>,
          replace: true,
        });
      }
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (llId: number) => {
      const displayId = AppContentUtils.getDisplayId(llId);
      navigate({ to: `${TAB_DETAIL_ROUTES[activeTab]}/${displayId}` as string });
    },
    [navigate, activeTab],
  );

  const handleDelete = useCallback(
    (row: ContentTableRow) => {
      const typeLabel = LISTLIST_TYPE_LABELS[TAB_TO_TYPE[activeTab]];
      modals.openConfirmModal({
        title: `Delete ${typeLabel}`,
        children: (
          <Text size="sm">
            Are you sure you want to delete &ldquo;{row.title}&rdquo;? This
            action cannot be undone.
          </Text>
        ),
        labels: { confirm: 'Delete', cancel: 'Cancel' },
        confirmProps: { color: 'red' },
        onConfirm: () => {
          deleteMutation.mutate(row.llId, {
            onSuccess: () => {
              notifications.show({
                title: 'Deleted',
                message: `${typeLabel} deleted successfully.`,
                color: 'green',
              });
            },
            onError: (err) => {
              notifications.show({
                title: 'Error',
                message: err.message,
                color: 'red',
              });
            },
          });
        },
      });
    },
    [activeTab, deleteMutation],
  );

  const handleCreate = useCallback(
    (data: ListlistFormSchemaData) => {
      const typeId = TAB_TO_TYPE[activeTab];
      const req = AppContentUtils.buildCreateReq(data.title, typeId);
      createMutation.mutate(req, {
        onSuccess: (created) => {
          setCreateDialogOpen(false);
          notifications.show({
            title: 'Created',
            message: `${LISTLIST_TYPE_LABELS[typeId]} created successfully.`,
            color: 'green',
          });
          // Navigate to detail page
          const displayId = AppContentUtils.getDisplayId(created.llr.llId);
          navigate({ to: `${TAB_DETAIL_ROUTES[activeTab]}/${displayId}` as string });
        },
        onError: (err) => {
          notifications.show({
            title: 'Error',
            message: err.message,
            color: 'red',
          });
        },
      });
    },
    [activeTab, createMutation, navigate],
  );

  if (isLoading) {
    return (
      <Stack align="center" justify="center" h={400}>
        <Loader size="lg" />
        <Text c="dimmed">Loading content...</Text>
      </Stack>
    );
  }

  if (isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        {error?.message ?? 'Failed to load content'}
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={2}>Apps Content</Title>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create {LISTLIST_TYPE_LABELS[TAB_TO_TYPE[activeTab]]}
        </Button>
      </Group>

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tabs.List>
          <Tabs.Tab
            value="groups"
            leftSection={<IconFolderOpen size={16} />}
          >
            Content Groups ({contentGroups.length})
          </Tabs.Tab>
          <Tabs.Tab
            value="categories"
            leftSection={<IconCategory size={16} />}
          >
            Categories ({categories.length})
          </Tabs.Tab>
          <Tabs.Tab
            value="series"
            leftSection={<IconListTree size={16} />}
          >
            Series ({series.length})
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="groups" pt="md">
          <AppContentTable
            rows={currentRows.groups}
            childLabel="Categories"
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </Tabs.Panel>

        <Tabs.Panel value="categories" pt="md">
          <AppContentTable
            rows={currentRows.categories}
            childLabel="Series"
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </Tabs.Panel>

        <Tabs.Panel value="series" pt="md">
          <AppContentTable
            rows={currentRows.series}
            childLabel="Seasons"
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </Tabs.Panel>
      </Tabs>

      <CreateListlistDialog
        opened={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreate}
        typeId={TAB_TO_TYPE[activeTab]}
        isPending={createMutation.isPending}
      />
    </Stack>
  );
}
